import { Box, Container, Typography, Grid, Divider } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArchitectureOutlinedIcon from '@mui/icons-material/ArchitectureOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { useRef } from 'react';
import CallToAction from '../CallToAction';
import { displayTitleSx } from '../../styles/typography';

const ProductsAndServices = () => {
  const trustedBrandsRef = useRef<HTMLDivElement>(null);
  const servicesForBuildersRef = useRef<HTMLDivElement>(null);
  
  const scrollToNextSection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const services = [
    'Reverse cycle ducted air conditioning',
    'Add-on air conditioning',
    'Split/Multi Split system air conditioning',
    'Evaporative cooling',
    'Ducted heating',
    'Panel heaters'
  ];

  // The "Built for Builders" promise, broken out of the original three paragraphs so
  // it can be scanned rather than read.
  const builderPoints = [
    {
      icon: <ArchitectureOutlinedIcon />,
      title: 'From slab to handover',
      description: 'We tailor our process to support your build at every stage, from slab through to handover.'
    },
    {
      icon: <GroupsOutlinedIcon />,
      title: 'Direct site coordination',
      description: 'Our team coordinates directly with your site supervisors so every installation happens on time.'
    },
    {
      icon: <VerifiedOutlinedIcon />,
      title: 'Checked on site',
      description: "We conduct quality checks on site throughout the build, so the installation meets Hallidays' internal standards."
    }
  ];

  const brands = [
    { name: 'Rinnai', description: "High-performance heating and cooling solutions designed for Australian conditions.", url: 'https://www.rinnai.com.au' },
    { name: 'Seeley International', description: 'Leaders in energy-efficient evaporative cooling, including Braemar, Breezair and Coolair.', url: 'https://www.seeleyinternational.com' },
    { name: 'ActronAir', description: 'Australian-made ducted systems, built tough for local climates.', url: 'https://www.actronair.com.au' },
    { name: 'Mitsubishi Electric', description: 'Smart, energy-efficient air conditioners with advanced technology.', url: 'https://www.mitsubishielectric.com.au' },
    { name: 'Fujitsu General', description: "Reliable air conditioning backed by Australia's Favourite Air brand.", url: 'https://www.fujitsugeneral.com.au' },
    { name: 'Daikin', description: 'Premium heating and cooling systems with world-class climate control.', url: 'https://www.daikin.com.au' },
    { name: 'Bonaire', description: 'Affordable and effective solutions for year-round comfort.', url: 'https://www.bonaire.com.au' },
    { name: 'Haier / Fisher & Paykel', description: 'Practical and modern systems ideal for residential builds.', url: 'https://www.haierhome.com.au' },
    { name: 'Panasonic', description: 'Energy-efficient air conditioning with innovative air purification features.', url: 'https://www.panasonic.com/au/hvac/user-top/residential.html' },
    { name: 'Advantage Air', description: 'Australian-made ducted air conditioning with MyAir and e-zone smart controls.', url: 'https://www.advantageair.com.au/' },
    { name: 'AirTouch', description: 'Smart zone control for ducted air conditioning, by Polyaire.', url: 'https://www.airtouch.net.au/' }
  ];

  return (
    <Box>
      {/* Hero Section with Intro and Services + Image */}
      <Box sx={{ 
        bgcolor: '#f8fbff',
        py: { xs: 6, md: 10 },
        position: 'relative'
      }}>
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: { xs: 4, md: 6 }
            }}
          >
            {/* Left Content */}
            <Box 
              sx={{ 
                flex: '1 1 0',
                width: { xs: '100%', sm: '50%' }
              }}
            >
              <Typography variant="h1" 
                sx={{
                  ...displayTitleSx,
                  color: '#0066b2',
                  fontWeight: 700,
                  mb: { xs: 2, md: 3 },
                }}
              >
                Products & Services
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 3,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  color: '#445668',
                  lineHeight: 1.6
                }}
              >
                At Hallidays, we specialise in the supply and installation of high-quality heating and cooling systems for residential builders across Victoria. From site consultation and design to rough-in, fit-off, and final commissioning, we ensure seamless coordination at every stage of construction.
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#445668',
                  mb: 2,
                  fontWeight: 600
                }}
              >
                We offer a comprehensive range of heating and cooling systems:
              </Typography>
              <Box component="ul" 
                sx={{ 
                  listStyle: 'none',
                  p: 0,
                  m: 0,
                  '& li': {
                    color: '#445668',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    '&::before': {
                      content: '""',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#0066b2',
                      mr: 2,
                      flexShrink: 0
                    }
                  }
                }}
              >
                {services.map((service, index) => (
                  <li key={index}>{service}</li>
                ))}
              </Box>
            </Box>
            
            {/* Right Content - Image */}
            <Box 
              sx={{ 
                flex: '1 1 0',
                width: { xs: '100%', sm: '50%' },
                alignSelf: 'flex-start',
                mt: { xs: 0, sm: 0, md: 0 },
              }}
            >
              <img
                src="/img/img-residential.jpg"
                alt="Products and Services"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  display: 'block'
                }}
              />
            </Box>
          </Box>
        </Container>
        
        {/* Scroll Down Indicator - Only visible on desktop */}
        <Box 
          sx={{ 
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'center',
            position: 'relative',
            bottom: 0,
            width: '100%',
            mt: 2,
            mb: -4,
            zIndex: 2
          }}
        >
          <Box 
            onClick={() => scrollToNextSection(trustedBrandsRef)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'transform 0.3s',
              '&:hover': {
                transform: 'translateY(5px)',
              },
            }}
          >
            <Typography 
              sx={{ 
                color: '#0066b2', 
                fontSize: '0.9rem',
                mb: 1,
                fontWeight: 500,
              }}
            >
              Scroll Down
            </Typography>
            <KeyboardArrowDownIcon 
              sx={{ 
                color: '#0066b2',
                fontSize: '2rem',
                animation: 'bounce 2s infinite',
                '@keyframes bounce': {
                  '0%, 20%, 50%, 80%, 100%': {
                    transform: 'translateY(0)',
                  },
                  '40%': {
                    transform: 'translateY(8px)',
                  },
                  '60%': {
                    transform: 'translateY(4px)',
                  },
                },
              }} 
            />
          </Box>
        </Box>
      </Box>
      
      {/* Trusted Brands Section */}
      <Box 
        ref={trustedBrandsRef}
        sx={{ 
          bgcolor: '#ffffff',
          py: { xs: 6, md: 8 },
          position: 'relative'
        }}
      >
        <Container maxWidth="xl">
          <Typography 
            variant="h2" 
            sx={{
              ...displayTitleSx,
              color: '#0066b2',
              fontWeight: 600,
              mb: { xs: 1, sm: 2 },
              textAlign: 'center'
            }}
          >
            Our Trusted Brands
          </Typography>
          
          {/* Grid rather than wrapped flex: gridAutoRows 1fr makes every card the
              same height across all rows, not just within a row. */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))'
            },
            gridAutoRows: '1fr',
            gap: 3
          }}>
            {brands.map((brand, index) => (
              <Box
                key={index}
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '8px',
                  border: '1px solid rgba(0, 102, 178, 0.1)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 102, 178, 0.08)',
                  }
                }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      color: '#0066b2'
                    }}
                  >
                    {brand.name}
                  </Typography>
                  <Typography paragraph sx={{ color: '#445668', mb: 2 }}>
                    {brand.description}
                  </Typography>
                  {/* mt: auto pins the link to the bottom so it lines up across cards */}
                  <Box sx={{ mt: 'auto' }}>
                    <a
                      href={brand.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#0066b2',
                        textDecoration: 'none',
                        fontWeight: 500,
                        fontSize: '0.9rem'
                      }}
                    >
                      Learn more →
                    </a>
                  </Box>
                </Box>
              ))}
          </Box>
          
          <Typography paragraph sx={{ color: '#445668', mt: 4, textAlign: 'center' }}>
            We also work with many other leading brands, depending on customer requirement and specifications.
          </Typography>
        </Container>
        
        {/* Scroll Down Indicator - Only visible on desktop */}
        <Box 
          sx={{ 
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'center',
            position: 'relative',
            bottom: 0,
            width: '100%',
            mt: 4,
            mb: -4,
            zIndex: 2
          }}
        >
          <Box 
            onClick={() => scrollToNextSection(servicesForBuildersRef)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'transform 0.3s',
              '&:hover': {
                transform: 'translateY(5px)',
              },
            }}
          >
            <Typography 
              sx={{ 
                color: '#0066b2', 
                fontSize: '0.9rem',
                mb: 1,
                fontWeight: 500,
              }}
            >
              Scroll Down
            </Typography>
            <KeyboardArrowDownIcon 
              sx={{ 
                color: '#0066b2',
                fontSize: '2rem',
                animation: 'bounce 2s infinite',
                '@keyframes bounce': {
                  '0%, 20%, 50%, 80%, 100%': {
                    transform: 'translateY(0)',
                  },
                  '40%': {
                    transform: 'translateY(8px)',
                  },
                  '60%': {
                    transform: 'translateY(4px)',
                  },
                },
              }} 
            />
          </Box>
        </Box>
      </Box>
      
      {/* Services for Builders Section */}
      <Box 
        ref={servicesForBuildersRef}
        sx={{ 
          bgcolor: '#f8fbff',
          py: { xs: 6, md: 8 },
          position: 'relative'
        }}
      >
        <Container maxWidth="xl">
          <Typography 
            variant="h2" 
            sx={{
              ...displayTitleSx,
              color: '#0066b2',
              fontWeight: 600,
              mb: { xs: 1, sm: 2 },
              textAlign: 'center'
            }}
          >
            Our Services: Built for Builders
          </Typography>
          
          <Typography
            sx={{
              color: '#445668',
              fontSize: { xs: '1.05rem', sm: '1.15rem' },
              lineHeight: 1.7,
              maxWidth: '760px',
              mx: 'auto',
              mb: { xs: 4, md: 6 },
              textAlign: 'center'
            }}
          >
            We take pride in the relationships we build and the quality of work we deliver.
          </Typography>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gridAutoRows: '1fr',
            gap: 3,
            maxWidth: '1100px',
            mx: 'auto'
          }}>
            {builderPoints.map((point, index) => (
              <Box
                key={index}
                sx={{
                  p: { xs: 3, md: 3.5 },
                  bgcolor: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 102, 178, 0.1)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 20px rgba(0, 102, 178, 0.08)',
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: 30,
                    color: '#0066b2'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    mb: 2,
                    borderRadius: '12px',
                    bgcolor: 'rgba(0, 102, 178, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {point.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: '#0066b2', mb: 1, fontSize: '1.15rem' }}
                >
                  {point.title}
                </Typography>
                <Typography sx={{ color: '#445668', lineHeight: 1.7 }}>
                  {point.description}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography
            sx={{
              mt: { xs: 4, md: 5 },
              maxWidth: '760px',
              mx: 'auto',
              textAlign: 'center',
              color: '#445668',
              fontSize: '1rem',
              lineHeight: 1.7
            }}
          >
            We've been doing heating and cooling for Victorian builders for a long time,
            and we care about your comfort.
          </Typography>
        </Container>
      </Box>

      {/* Same shared CTA as Home and Builders */}
      <CallToAction title="Contact us today to discuss how we can support your next project" />
    </Box>
  );
};

export default ProductsAndServices;